import axios from "axios";

export const adminFetchProductsApi = async (
  token,
  signal,
  sortKey = false,
  pageNr = null,
  filterKey = false
) => {
  // fetches all existing products from the server, with optional sorting/filters applied
  // token (String): bearer token used for authentication
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  // sortKey (String): the attribute to sort on, eg "date_1"
  // pageNr (Number): the new page nr requested by the user (pagination)
  // filterKey (String): the attribute to filter on, eg "inStock_0"
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    // create query params string
    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    if (sortKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `sortKey=${sortKey}`;
    }
    if (filterKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      const filterArr = filterKey.split("_");
      if (filterArr[0] === "inStock") {
        queryParams += `inStock=${filterArr[1]}`;
      } else {
        queryParams += `filterKey=${filterKey}`;
      }
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/products/admin${queryParams}`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminCreateProductApi = async (token, productData, signal) => {
  // post request to the server to create a new product
  // token (String): bearer token used for authentication
  // productData (JSON): the data for the new product
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/products`, productData, config, {
      signal,
    });
    return data;
  } catch (error) {
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminUpdateProductApi = async (token, id, productData, signal) => {
  // put request to the server to update an existing product
  // token (String): bearer token used for authentication
  // id (String): the id of the product to be updated
  // productData (JSON): the data to update the product with
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/products/${id}`,
      productData,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminAddImagesApi = async (token, id, newImages) => {
  // post request to the server to update the images for a specified product
  // token (String): bearer token used for authentication
  // id (String): the id of the product to be updated
  // newImages (JSON Array): objects with { url (String), publicId (String)}
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/products/images/${id}`,
      newImages,
      config
    );
    return data;
  } catch (error) {
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminDeleteImagesApi = async (token, id, publicIdsArray) => {
  // delete request to the server to delete images from a product
  // token (String): bearer token used for authentication
  // id (String): the id of the product to be updated
  // uplicIdsArray (String Array): the publics ids of the images to be deleted
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const publicIdsString = publicIdsArray.join(",");
    const { data } = await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/products/images/${id}?publicIds=${publicIdsString}`,
      config
    );
    return data;
  } catch (error) {
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminDeleteProductApi = async (token, productId, signal) => {
  // delete request to the server to delete a product
  // token (String): bearer token used for authentication
  // productId (String): the id of the product to be deleted
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.delete(`${process.env.REACT_APP_API_URL}/api/products/${productId}`, config, {
      signal,
    });
    return data;
  } catch (error) {
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchProductsApi = async (
  categoryId = "",
  ratingsString = "",
  maxPrice = "",
  attributes = "",
  sortString = "",
  pageNr = "",
  searchText = ""
) => {
  // fetches all existing products from the server, with optional sorting/filters applied
  // categoryId (String): the id of the category to be used for filtering
  // ratingsString (String): the ratings "2,3,4" to be used for filtering
  // maxPrice (String): the max price to be used for filtering
  // attributes (String): the attributes to be used for filtering
  // sortString (String): the attribute to be used for sorting
  // pageNr (Number): the new page nr requested by the user (pagination)
  // searchText (String): the search text to be used for filtering
  try {
    const categoryPathParam = categoryId ? `/category/${categoryId}` : "";
    // query params
    let queryParams = "";
    queryParams = ratingsString ? `?ratings=${ratingsString}` : "";
    if (maxPrice) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `price=${maxPrice}`;
    }
    if (attributes) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `attributes=${attributes}`;
    }
    if (sortString) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `sortKey=${sortString}`;
    }
    if (pageNr) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `pageNr=${pageNr}`;
    }
    if (searchText) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams += `searchText=${searchText}`;
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/products${categoryPathParam}${queryParams}`
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchProductApi = async (id, signal) => {
  // fetches product details for the product with the specified id
  // id (String): the id of the product to be fetched
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`, { signal });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminFetchProductApi = async (token, id, signal) => {
  // fetches product details for the product with the specified id
  // token (String): bearer token used for authentication
  // id (String): the id of the product to be fetched
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/admin/${id}`, config, {
      signal,
    });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchProductsByIdsApi = async (productIds, signal) => {
  // fetches all products that have one of the specified ids
  // productIds (String Array): array of product ids
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/products/retrieve`,
      { products: productIds },
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchBestSellersApi = async (signal) => {
  // fetches the bestselling products, based on nrs sold
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/bestsellers`, { signal });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};
